import React from 'react';
import styles from './Download.module.css';
import { DOWNLOAD_OPTION_1, DOWNLOAD_OPTION_2, DISCORD_URL } from '../config';
import useScrollAnimation from '../hooks/useScrollAnimation';

const MIN_SPECS = [
  { label: 'OS', value: 'Windows 7 / 8.1 / 10 (32 or 64-bit)' },
  { label: 'CPU', value: 'Intel Pentium 4 2.0 GHz or equivalent' },
  { label: 'RAM', value: '1 GB' },
  { label: 'GPU', value: 'GeForce 4 Ti / Radeon 9200 or equivalent' },
  { label: 'Storage', value: '~15 GB available space' },
  { label: 'DirectX', value: 'Version 9.0c' },
  { label: 'Network', value: 'Broadband Internet connection' },
];

const REC_SPECS = [
  { label: 'OS', value: 'Windows 10 / 11 (64-bit)' },
  { label: 'CPU', value: 'Intel Core i5 2.6 GHz or equivalent' },
  { label: 'RAM', value: '4 GB' },
  { label: 'GPU', value: 'GeForce GTX 760 / Radeon R9 270 or equivalent' },
  { label: 'Storage', value: '~20 GB SSD' },
  { label: 'DirectX', value: 'Version 11' },
  { label: 'Network', value: 'Broadband Internet connection' },
];

export default function Download() {
  const [ref, visible] = useScrollAnimation();

  return (
    <main className={styles.main}>
      <section className={`page-hero ${styles.hero}`} aria-label="Download hero">
        <h1>Download Client</h1>
        <p className={styles.heroSub}>Get the official Lineage II Community client and begin your adventure</p>
      </section>

      <div ref={ref} className={`reveal ${visible ? 'visible' : ''} ${styles.container}`}>
        {/* Client Download */}
        <div className={styles.clientCard}>
          <div className={styles.clientInfo}>
            <h2 className={styles.clientTitle}>Lineage II Community Client</h2>
            <p className={styles.clientMeta}>
              <span className={styles.metaItem}>Lineage II Guardians</span>
              <span className={styles.metaDot} aria-hidden="true" />
              <span className={styles.metaItem}>~15 GB</span>
              <span className={styles.metaDot} aria-hidden="true" />
              <span className={styles.metaItem}>Windows Only</span>
            </p>
            <p className={styles.clientDesc}>
              The full Lineage II Community game client. Includes all required game files,
              patched executables, and launcher. This is everything you need to connect to
              the server and begin your adventure.
            </p>
          </div>

          <div className={styles.downloadOptions}>
            <p className={styles.optionsLabel}>Choose your download mirror:</p>
            <div className={styles.optionsRow}>
              <a
                href={DOWNLOAD_OPTION_2}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.downloadBtn}
                aria-label="Download via Google Drive - Option 1"
              >
                Option 1 - Google Drive
              </a>
              <a
                href={DOWNLOAD_OPTION_1}
                target="_blank"
                rel="noopener noreferrer"
                className={`${styles.downloadBtn} ${styles.downloadBtnOutline}`}
                aria-label="Download via OneDrive - Option 2"
              >
                Option 2 - OneDrive
              </a>
            </div>
          </div>
        </div>

        {/* System Download — required, hosted on Discord */}
        <div className={styles.clientCard}>
          <div className={styles.clientInfo}>
            <h2 className={styles.clientTitle}>
              System Files
              <span className={styles.requiredBadge}>Required</span>
            </h2>
            <p className={styles.clientMeta}>
              <span className={styles.metaItem}>Game Patch</span>
              <span className={styles.metaDot} aria-hidden="true" />
              <span className={styles.metaItem}>Windows Only</span>
            </p>
            <p className={styles.clientDesc}>
              Required patch files for connecting to the Lineage II Community server.
              Download and apply these files before launching the game. Available exclusively
              in our Discord server.
            </p>
          </div>

          <div className={styles.downloadOptions}>
            <p className={styles.optionsLabel}>Download from Discord:</p>
            <div className={styles.optionsRow}>
              <a
                href="https://discord.gg/4xNcVmqw"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.downloadBtn}
                aria-label="Download system files from Discord"
              >
                Get on Discord
              </a>
            </div>
          </div>
        </div>

        <div className={styles.specsSection}>
          <h2 className={styles.specsTitle}>System Requirements</h2>
          <hr className="gold-divider" />
          <div className={styles.specsGrid}>
            <div className={styles.specsCard}>
              <h3 className={styles.specsHeading}>Minimum</h3>
              <table className={styles.specsTable}>
                <tbody>
                  {MIN_SPECS.map(({ label, value }) => (
                    <tr key={label} className={styles.specRow}>
                      <th scope="row" className={styles.specLabel}>{label}</th>
                      <td className={styles.specValue}>{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={`${styles.specsCard} ${styles.specsCardHighlight}`}>
              <h3 className={styles.specsHeading}>Recommended</h3>
              <table className={styles.specsTable}>
                <tbody>
                  {REC_SPECS.map(({ label, value }) => (
                    <tr key={label} className={styles.specRow}>
                      <th scope="row" className={styles.specLabel}>{label}</th>
                      <td className={styles.specValue}>{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className={styles.installNote}>
          <h3 className={styles.noteTitle}>Installation Notes</h3>
          <ul className={styles.noteList}>
            <li>Extract the downloaded archive to your desired folder.</li>
            <li>Run the launcher as Administrator for best compatibility.</li>
            <li>Ensure your antivirus does not block the game executable.</li>
            <li>
              Need help?{' '}
              <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" className={styles.noteLink}>
                Join our Discord
              </a>{' '}
              for support.
            </li>
          </ul>
        </div>
      </div>
    </main>
  );
}
